import { useMemo } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DASHBOARD_COLORS } from "@/lib/dashboardColors";
import { formatCurrency } from "@/utils/format";

const RevenueLegend = () => (
  <div className="flex flex-wrap items-center gap-4 pt-2 text-xs text-slate-500" data-testid="revenue-expenses-legend">
    <div className="flex items-center gap-2">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: DASHBOARD_COLORS.revenue }}
      />
      Einnahmen netto
    </div>
    <div className="flex items-center gap-2">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: DASHBOARD_COLORS.revenueLight }}
      />
      Einnahmen USt
    </div>
    <div className="flex items-center gap-2">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: DASHBOARD_COLORS.expenses }}
      />
      Ausgaben
    </div>
  </div>
);

const RevenueExpensesTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const revenueNet = payload.find((item) => item.dataKey === "revenueNet")?.value || 0;
  const revenueVat = payload.find((item) => item.dataKey === "revenueVat")?.value || 0;
  const expenseTotal = payload.find((item) => item.dataKey === "expenseTotal")?.value || 0;
  const revenueGross = revenueNet + revenueVat;

  return (
    <div className="rounded-lg border bg-white p-3 text-xs text-gray-700 shadow-sm">
      <div className="font-semibold text-gray-900">{label}</div>
      <div>Einnahmen brutto: {formatCurrency(revenueGross)}</div>
      <div>Einnahmen netto: {formatCurrency(revenueNet)}</div>
      <div>USt: {formatCurrency(revenueVat)}</div>
      <div>Ausgaben: {formatCurrency(expenseTotal)}</div>
    </div>
  );
};

export default function RevenueExpensesChart({ data = [], isLoading, error }) {
  const gridColor = useMemo(() => {
    if (typeof document === "undefined") return "#e2e8f0";
    return document.documentElement.classList.contains("dark") ? "#334155" : "#e2e8f0";
  }, []);
  const axisColor = "#475569";

  return (
    <Card data-testid="revenue-expenses-chart">
      <CardHeader>
        <CardTitle>Umsatz vs. Ausgaben</CardTitle>
      </CardHeader>
      <CardContent className="h-72">
        {isLoading && <Skeleton className="h-full w-full" data-testid="revenue-expenses-loading" />}
        {!isLoading && error && (
          <div className="text-sm text-red-600" data-testid="revenue-expenses-error">
            Chart-Daten konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !error && data.length === 0 && (
          <div className="text-sm text-gray-500" data-testid="revenue-expenses-empty">
            Keine Daten im gewählten Zeitraum.
          </div>
        )}
        {!isLoading && !error && data.length > 0 && (
          <div className="flex h-full flex-col">
            <div className="flex-1">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={200}>
                <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke={axisColor} tick={{ fill: axisColor }} />
                  <YAxis
                    stroke={axisColor}
                    tick={{ fill: axisColor }}
                    width={90}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip content={<RevenueExpensesTooltip />} />
                  <Bar
                    dataKey="revenueNet"
                    stackId="income"
                    fill={DASHBOARD_COLORS.revenue}
                    name="Einnahmen netto"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="revenueVat"
                    stackId="income"
                    fill={DASHBOARD_COLORS.revenueLight}
                    name="Einnahmen USt"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="expenseTotal"
                    stackId="expenses"
                    fill={DASHBOARD_COLORS.expenses}
                    name="Ausgaben"
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <RevenueLegend />
          </div>
        )}
      </CardContent>
    </Card>
  );
}